import React, { Suspense, useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./auth/AuthProvider";
import { useAuth } from "./auth/useAuth";
import { SessionProvider } from "./session/SessionProvider";
import AppToastContainer from "./components/AppToastContainer";
import { MenuProvider, useMenu } from "./context/MenuContext";
import {
  getAppForPath,
  isImmersivePath,
  isFooterHiddenPath,
} from "./config/appRegistry";
import {
  setAmplitudeUser,
  resetAmplitudeUser,
  trackEvent,
  startSessionReplay,
  stopSessionReplay,
} from "./analytics/amplitude";
import ProtectedRoute from "./auth/ProtectedRoute";
import MainLayout from "./layouts/MainLayout";
import AppLoading from "./components/AppLoading";
import AuthLayout from "./layouts/AuthLayout";
import ForgotPassword from "./pages/ForgotPassword";
import LoginForm from "./pages/LoginForm";
import ResetPassword from "./pages/ResetPassword";
import VerifyOtp from "./pages/VerifyOtp";

const SophosApp = React.lazy(() => import("sophos/App"));
const HeliosApp = React.lazy(() => import("helios/App"));
const KleioApp = React.lazy(() => import("kleio/App"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 2,
      staleTime: 1000 * 5,
    },
  },
});

function MainWithMenus({ currentApp, immersive, hideFooter }) {
  const { menus, loading } = useMenu();
  const [loaded, setLoaded] = useState(() => new Set([currentApp]));

  useEffect(() => {
    if (currentApp) setLoaded((prev) => new Set([...prev, currentApp]));
  }, [currentApp]);

  const shell = (app) =>
    `${app}-mfe${immersive && currentApp === app ? " h-full" : ""}`;

  return (
    <MainLayout immersive={immersive} hideFooter={hideFooter}>
      <div
        className={shell("sophos")}
        style={{ display: currentApp === "sophos" ? "" : "none" }}
      >
        {loaded.has("sophos") && (
          <Suspense fallback={<AppLoading />}>
            <SophosApp
              standalone={false}
              active={currentApp === "sophos"}
              menus={menus}
              menuLoading={loading}
            />
          </Suspense>
        )}
      </div>
      <div
        className={shell("helios")}
        style={{ display: currentApp === "helios" ? "" : "none" }}
      >
        {loaded.has("helios") && (
          <Suspense fallback={<AppLoading />}>
            <HeliosApp
              standalone={false}
              active={currentApp === "helios"}
              menus={menus}
              menuLoading={loading}
            />
          </Suspense>
        )}
      </div>
      <div
        className={shell("kleio")}
        style={{ display: currentApp === "kleio" ? "" : "none" }}
      >
        {loaded.has("kleio") && (
          <Suspense fallback={<AppLoading />}>
            <KleioApp
              standalone={false}
              active={currentApp === "kleio"}
              menus={menus}
              menuLoading={loading}
            />
          </Suspense>
        )}
      </div>
    </MainLayout>
  );
}

function AppContent() {
  const location = useLocation();
  const path = location.pathname;
  const { isAuthenticated, user, logout } = useAuth();

  const prevAuthenticated = React.useRef(isAuthenticated);

  useEffect(() => {
    if (isAuthenticated) {
      startSessionReplay();
    } else {
      stopSessionReplay();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated && user) {
      setAmplitudeUser(user);
    } else if (prevAuthenticated.current && !isAuthenticated) {
      trackEvent("Logout");
      resetAmplitudeUser();
    }

    prevAuthenticated.current = isAuthenticated;
  }, [isAuthenticated, user, logout]);

  // Case-insensitive: email links use /Account/ResetPassword.
  const lowerPath = path.toLowerCase();
  const isAuthPath =
    path === "/" ||
    lowerPath.startsWith("/forgot-password") ||
    lowerPath.startsWith("/verify-otp") ||
    lowerPath.startsWith("/reset-password") ||
    lowerPath === "/account/resetpassword" ||
    lowerPath.startsWith("/account/resetpassword/");
  if (isAuthPath) {
    if (isAuthenticated) {
      return <Navigate to="/search" replace />;
    }
    return (
      <Routes>
        <Route path="/" element={<AuthLayout />}>
          <Route index element={<LoginForm />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="verify-otp" element={<VerifyOtp />} />
        </Route>
        {/* Pathless parent so absolute reset paths always match. */}
        <Route element={<AuthLayout />}>
          <Route path="/reset-password/*" element={<ResetPassword />} />
          <Route
            path="/Account/ResetPassword/*"
            caseSensitive={false}
            element={<ResetPassword />}
          />
        </Route>
      </Routes>
    );
  }

  const currentApp = getAppForPath(path);

  return (
    <ProtectedRoute>
      <MenuProvider>
        <MainWithMenus
          currentApp={currentApp}
          immersive={isImmersivePath(path)}
          hideFooter={isFooterHiddenPath(path)}
        />
      </MenuProvider>
    </ProtectedRoute>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <AppToastContainer />
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <SessionProvider>
            <AppContent />
          </SessionProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </AuthProvider>
  );
}
